import React, { useCallback, useRef } from 'react';
import { useTranslation, Trans as TransComponent } from 'react-i18next';
import { toLocalizedUrl } from 'tradera-utils/url';
import {
    NS_TOUCHWEB,
    I18N_FRONTEND_NAMESPACES,
    TRANS_SUFFIX
} from './constants.mjs';
import { addKey } from 'tradera-backend/i18n/translation-keys.js';
import { isServer } from 'tradera-utils/nextjs.js';
import { useSelector } from 'react-redux';
import { selectPageType } from 'tradera-state/page/selectors';

/**
 * i18next translation wrappers for translating static text in react components
 * When to use what?
 * -----
 * 1. For functional components use `useTranslator()` hook
 * 2. For class components use `withTranslator()` HOC
 */

/**
 * Translation hook, wrapper for react-i18next
 * @return {{t: Function}}
 */

export const useTranslator = () => {
    const pathType = useSelector(selectPageType);
    const { t, ready } = useTranslation(I18N_FRONTEND_NAMESPACES, {
        wait: true
    });
    const wrappedT = useCallback(
        (...args) => {
            const i18nKey = args[0];
            if (i18nKey.endsWith(TRANS_SUFFIX)) {
                throw new Error(
                    `t function keys should not have "Trans" suffix, got key "${i18nKey}". Use Trans component instead.`
                );
            }
            if (isServer) {
                const options = args[1];
                const namespace = options?.ns || NS_TOUCHWEB;
                addKey(pathType, namespace, i18nKey);
            }
            return t(...args);
        },
        [t, pathType]
    );
    return { t: ready ? wrappedT : () => '...' };
};

/**
 * Translation HOC, wrapper for react-i18next
 * @param {Component} WrappedComponent
 * @return {Component} The wrapped component.
 */
export function withTranslator(WrappedComponent) {
    function WithTranslator(props) {
        const { t } = useTranslator();
        return <WrappedComponent t={t} {...props} />;
    }

    WithTranslator.displayName = `withTranslator(${WrappedComponent.displayName ||
        WrappedComponent.name}`;
    return WithTranslator;
}

/**
 * Localization HOC, wrapper for URL-localizer
 * @param {Component} WrappedComponent
 */
export function withUrlLocalizer(WrappedComponent) {
    function WithUrlLocalizer(props) {
        const { toLocalizedUrl } = useUrlLocalizer();
        return <WrappedComponent toLocalizedUrl={toLocalizedUrl} {...props} />;
    }

    WithUrlLocalizer.displayName = `withUrlLocalizer(${WrappedComponent.displayName ||
        WrappedComponent.name}`;

    return WithUrlLocalizer;
}

/**
 * Current language hook, wrapper for react-i18next
 * @return {{language: string}}
 */
export const useCurrentLanguage = () => {
    const { i18n, ready } = useTranslation(undefined, { wait: true });
    return {
        language: ready ? i18n.language : null
    };
};

/**
 * Returns function to append language prefix to link
 * @return {{toLocalizedUrl: function}}
 */
export const useUrlLocalizer = () => {
    const { i18n, ready } = useTranslation(undefined, { wait: true });
    const urlLocalizer = useRef(url => toLocalizedUrl(url, i18n.language));
    return { toLocalizedUrl: ready ? urlLocalizer.current : url => url };
};

export const Trans = props => {
    const { t, ready } = useTranslation(I18N_FRONTEND_NAMESPACES, {
        wait: true
    });
    const { i18nKey, children, defaults } = props;
    if (!i18nKey) {
        throw new Error('Trans component key must have a i18nKey property');
    }
    if (!i18nKey.endsWith(TRANS_SUFFIX)) {
        throw new Error(
            `Trans component key must have the "Trans" suffix, got key "${i18nKey}"`
        );
    }
    if (children) {
        throw new Error(
            `Trans component must not have children, use components or t('${i18nKey}') function instead`
        );
    }
    if (defaults) {
        throw new Error(
            'Trans components must not have defaults, use lokalise instead.'
        );
    }
    if (!ready) {
        return '...';
    }
    return <TransComponent ns={NS_TOUCHWEB} t={t} {...props} />;
};
